import React, { useEffect, useState } from 'react';
import { XIcon, ChevronDownIcon, ChevronRightIcon, SearchIcon, UsersIcon, CalendarIcon, PaletteIcon, AnchorIcon, PencilIcon, PhoneIcon, UserIcon } from 'lucide-react';
interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}
export function MobileNavigation({
  isOpen,
  onClose
}: MobileNavigationProps) {
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [expandedSubMenu, setExpandedSubMenu] = useState<string | null>(null);
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  // Close expanded menus when main menu closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setExpandedMenu(null);
        setExpandedSubMenu(null);
        setActiveRegion(null);
      }, 300);
    }
  }, [isOpen]);
  const toggleMenu = (menu: string) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
    setExpandedSubMenu(null);
  };
  const toggleSubMenu = (subMenu: string) => {
    setExpandedSubMenu(expandedSubMenu === subMenu ? null : subMenu);
  };
  const toggleRegion = (region: string) => {
    setActiveRegion(activeRegion === region ? null : region);
  };
  // Define regions and countries for destinations menu
  const regions = [{
    name: 'Europe',
    countries: ['Austria', 'Belgium', 'Croatia', 'Czech Republic', 'Denmark', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Netherlands', 'Norway', 'Poland', 'Portugal', 'Spain', 'Sweden', 'Switzerland', 'United Kingdom']
  }, {
    name: 'Asia',
    countries: ['China', 'Japan', 'South Korea', 'Thailand', 'Vietnam', 'Singapore', 'Malaysia', 'Indonesia', 'Philippines', 'India']
  }, {
    name: 'Africa',
    countries: ['South Africa', 'Morocco', 'Egypt', 'Kenya', 'Tanzania']
  }, {
    name: 'Americas',
    countries: ['United States', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Peru', 'Chile']
  }, {
    name: 'Oceania',
    countries: ['Australia', 'New Zealand', 'Fiji']
  }, {
    name: 'Middle East',
    countries: ['United Arab Emirates', 'Qatar', 'Oman', 'Jordan', 'Israel']
  }];
  return <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} data-id="element-919"></div>}
      {/* Slide-out menu */}
      <div className={`fixed inset-y-0 left-0 w-full max-w-sm bg-white z-50 transform transition-transform duration-300 ease-in-out lg:hidden overflow-hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-920">
        <div className="flex flex-col h-full" data-id="element-921">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-922">
            <a href="/" className="flex-shrink-0" data-id="element-923">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dGmrTFD4H9KRDNXV9TQzMi/image.png" alt="EU Holidays Logo" className="h-8" data-id="element-924" />
            </a>
            <button onClick={onClose} className="p-2 rounded-md text-gray-600 hover:text-gray-900" aria-label="Close menu" data-id="element-925">
              <XIcon size={24} data-id="element-926" />
            </button>
          </div>
          {/* Search bar */}
          <div className="p-4 border-b border-gray-200" data-id="element-927">
            <div className="relative" data-id="element-928">
              <input type="text" placeholder="Search destinations, tours..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-929" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-930" />
            </div>
          </div>
          {/* Menu content - scrollable */}
          <div className="flex-1 overflow-y-auto" data-id="element-931">
            <nav className="py-2" data-id="element-932">
              {/* Plan Your Journey */}
              <div className="border-b border-gray-200" data-id="element-933">
                <button className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" onClick={() => toggleMenu('journey')} data-id="element-934">
                  <span data-id="element-935">Plan Your Journey</span>
                  <ChevronDownIcon size={20} className={`transition-transform ${expandedMenu === 'journey' ? 'rotate-180' : ''}`} data-id="element-936" />
                </button>
                {expandedMenu === 'journey' && <div className="px-4 pb-4 space-y-4" data-id="element-937">
                    <div className="space-y-3" data-id="element-938">
                      <a href="#" className="flex items-start group" data-id="element-939">
                        <div className="mr-3 p-2 bg-blue-100 rounded-full text-blue-600" data-id="element-940">
                          <UsersIcon size={20} data-id="element-941" />
                        </div>
                        <div data-id="element-942">
                          <div className="font-medium text-gray-900" data-id="element-943">
                            Group Holidays
                            <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full" data-id="element-944">
                              Most Popular
                            </span>
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-945">
                            Travel with like-minded explorers
                          </p>
                        </div>
                      </a>
                      <a href="#" className="flex items-start group" data-id="element-946">
                        <div className="mr-3 p-2 bg-purple-100 rounded-full text-purple-600" data-id="element-947">
                          <CalendarIcon size={20} data-id="element-948" />
                        </div>
                        <div data-id="element-949">
                          <div className="font-medium text-gray-900" data-id="element-950">
                            Flexi Holidays
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-951">
                            Travel at your own pace
                          </p>
                        </div>
                      </a>
                      <a href="#" className="flex items-start group" data-id="element-952">
                        <div className="mr-3 p-2 bg-green-100 rounded-full text-green-600" data-id="element-953">
                          <PaletteIcon size={20} data-id="element-954" />
                        </div>
                        <div data-id="element-955">
                          <div className="font-medium text-gray-900" data-id="element-956">
                            Themed Journeys
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-957">
                            Food, culture, photography and more
                          </p>
                        </div>
                      </a>
                      <a href="#" className="flex items-start group" data-id="element-958">
                        <div className="mr-3 p-2 bg-cyan-100 rounded-full text-cyan-600" data-id="element-959">
                          <AnchorIcon size={20} data-id="element-960" />
                        </div>
                        <div data-id="element-961">
                          <div className="font-medium text-gray-900" data-id="element-962">
                            Cruise Holidays
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-963">
                            Explore the world's waterways
                          </p>
                        </div>
                      </a>
                      <a href="#" className="flex items-start group" data-id="element-964">
                        <div className="mr-3 p-2 bg-amber-100 rounded-full text-amber-600" data-id="element-965">
                          <PencilIcon size={20} data-id="element-966" />
                        </div>
                        <div data-id="element-967">
                          <div className="font-medium text-gray-900" data-id="element-968">
                            Custom Itineraries
                          </div>
                          <p className="text-sm text-gray-600" data-id="element-969">
                            Tailored to your preferences
                          </p>
                        </div>
                      </a>
                    </div>
                    <div className="pt-3 border-t border-gray-200" data-id="element-970">
                      <h4 className="font-medium text-gray-900 mb-2" data-id="element-971">
                        Quick Links
                      </h4>
                      <ul className="space-y-2" data-id="element-972">
                        <li data-id="element-973">
                          <a href="#" className="block text-gray-700 hover:text-blue-600" data-id="element-974">
                            Why Travel With Us
                          </a>
                        </li>
                        <li data-id="element-975">
                          <a href="#" className="block text-gray-700 hover:text-blue-600" data-id="element-976">
                            Travel Insurance
                          </a>
                        </li>
                        <li data-id="element-977">
                          <a href="#" className="block text-gray-700 hover:text-blue-600" data-id="element-978">
                            Visa Services
                          </a>
                        </li>
                        <li data-id="element-979">
                          <a href="#" className="block text-gray-700 hover:text-blue-600" data-id="element-980">
                            Travel Guidelines
                          </a>
                        </li>
                        <li data-id="element-981">
                          <a href="#" className="block text-gray-700 hover:text-blue-600" data-id="element-982">
                            Book Consultation
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>}
              </div>
              {/* Destinations */}
              <div className="border-b border-gray-200" data-id="element-983">
                <button className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" onClick={() => toggleMenu('destinations')} data-id="element-984">
                  <span data-id="element-985">Destinations</span>
                  <ChevronDownIcon size={20} className={`transition-transform ${expandedMenu === 'destinations' ? 'rotate-180' : ''}`} data-id="element-986" />
                </button>
                {expandedMenu === 'destinations' && <div className="px-4 pb-4" data-id="element-987">
                    <div className="relative mb-4" data-id="element-988">
                      <input type="text" placeholder="Search destinations..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-989" />
                      <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-990" />
                    </div>
                    <div className="space-y-2" data-id="element-991">
                      {regions.map(region => <div key={region.name} className="border border-gray-200 rounded-md overflow-hidden" data-id="element-992">
                          <button className="flex items-center justify-between w-full p-3 text-left text-gray-900 hover:bg-gray-50" onClick={() => toggleRegion(region.name)} data-id="element-993">
                            <span className="font-medium" data-id="element-994">{region.name}</span>
                            <div className="flex items-center text-gray-500 text-sm" data-id="element-995">
                              <span data-id="element-996">{region.countries.length} countries</span>
                              <ChevronDownIcon size={18} className={`ml-1 transition-transform ${activeRegion === region.name ? 'rotate-180' : ''}`} data-id="element-997" />
                            </div>
                          </button>
                          {activeRegion === region.name && <div className="px-3 pb-3" data-id="element-998">
                              <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-999">
                                {region.countries.map(country => <a key={country} href={`/country/${country.toLowerCase().replace(/\s+/g, '-')}`} className="text-sm py-1.5 px-2 rounded hover:bg-gray-100 text-gray-700" data-id="element-1000">
                                    {country}
                                  </a>)}
                              </div>
                            </div>}
                        </div>)}
                    </div>
                  </div>}
              </div>
              {/* Deals & Offers */}
              <div className="border-b border-gray-200" data-id="element-1001">
                <button className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" onClick={() => toggleMenu('deals')} data-id="element-1002">
                  <span data-id="element-1003">Deals & Offers</span>
                  <ChevronDownIcon size={20} className={`transition-transform ${expandedMenu === 'deals' ? 'rotate-180' : ''}`} data-id="element-1004" />
                </button>
                {expandedMenu === 'deals' && <div className="px-4 pb-4" data-id="element-1005">
                    <ul className="space-y-2" data-id="element-1006">
                      <li data-id="element-1007">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1008">
                          Early Bird Offers
                        </a>
                      </li>
                      <li data-id="element-1009">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1010">
                          Last Minute Deals
                        </a>
                      </li>
                      <li data-id="element-1011">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1012">
                          Group Discounts
                        </a>
                      </li>
                      <li data-id="element-1013">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1014">
                          Seasonal Specials
                        </a>
                      </li>
                      <li data-id="element-1015">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1016">
                          Member-Only Offers
                        </a>
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Travel Club */}
              <div className="border-b border-gray-200" data-id="element-1017">
                <a href="/travel-club" className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" data-id="element-1018">
                  <span data-id="element-1019">Travel Club</span>
                  <ChevronRightIcon size={20} className="text-gray-500" data-id="element-1020" />
                </a>
              </div>
              {/* About Us */}
              <div className="border-b border-gray-200" data-id="element-1021">
                <button className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" onClick={() => toggleMenu('about')} data-id="element-1022">
                  <span data-id="element-1023">About Us</span>
                  <ChevronDownIcon size={20} className={`transition-transform ${expandedMenu === 'about' ? 'rotate-180' : ''}`} data-id="element-1024" />
                </button>
                {expandedMenu === 'about' && <div className="px-4 pb-4" data-id="element-1025">
                    <ul className="space-y-2" data-id="element-1026">
                      <li data-id="element-1027">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1028">
                          Our Story
                        </a>
                      </li>
                      <li data-id="element-1029">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1030">
                          Team
                        </a>
                      </li>
                      <li data-id="element-1031">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1032">
                          Testimonials
                        </a>
                      </li>
                      <li data-id="element-1033">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1034">
                          Careers
                        </a>
                      </li>
                      <li data-id="element-1035">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1036">
                          Press & Media
                        </a>
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Our Brands */}
              <div className="border-b border-gray-200" data-id="element-1037">
                <button className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" onClick={() => toggleMenu('brands')} data-id="element-1038">
                  <span data-id="element-1039">Our Brands</span>
                  <ChevronDownIcon size={20} className={`transition-transform ${expandedMenu === 'brands' ? 'rotate-180' : ''}`} data-id="element-1040" />
                </button>
                {expandedMenu === 'brands' && <div className="px-4 pb-4" data-id="element-1041">
                    <ul className="space-y-2" data-id="element-1042">
                      <li data-id="element-1043">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1044">
                          EU Holidays
                        </a>
                      </li>
                      <li data-id="element-1045">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1046">
                          EU Cruise
                        </a>
                      </li>
                      <li data-id="element-1047">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1048">
                          EU Asia
                        </a>
                      </li>
                      <li data-id="element-1049">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1050">
                          EU Exotic
                        </a>
                      </li>
                      <li data-id="element-1051">
                        <a href="#" className="block py-2 text-gray-700 hover:text-blue-600" data-id="element-1052">
                          Partner Agencies
                        </a>
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Contact */}
              <div className="border-b border-gray-200" data-id="element-1053">
                <a href="/contact" className="flex items-center justify-between w-full p-4 text-left text-gray-900 font-medium" data-id="element-1054">
                  <span data-id="element-1055">Contact</span>
                  <ChevronRightIcon size={20} className="text-gray-500" data-id="element-1056" />
                </a>
              </div>
            </nav>
          </div>
          {/* Footer actions */}
          <div className="p-4 border-t border-gray-200" data-id="element-1057">
            <div className="grid grid-cols-2 gap-3" data-id="element-1058">
              <a href="#" className="flex items-center justify-center py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1059">
                <UserIcon size={16} className="mr-2" data-id="element-1060" />
                <span data-id="element-1061">Account</span>
              </a>
              <a href="#" className="flex items-center justify-center py-2 px-4 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors" data-id="element-1062">
                <PhoneIcon size={16} className="mr-2" data-id="element-1063" />
                <span data-id="element-1064">Contact Us</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>;
}